import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useAppTheme } from "@hooks/useAppTheme";
/* -----------------Types--------------- */
import { getSortedChoices } from "./helper";

import { events } from "@constants/tracking";
/* -----------------Icons--------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppStore } from "@hooks/useAppStore";
import TTSContainer from "@memorang/features/src/TTS/TTSContainer";
import { findFeedbackByLanguage } from "@memorang/helpers/feedback";
import type { Choice, ItemFeedback, Media } from "@memorang/types";
/* -----------------Components--------------- */
import Box from "@memorang/ui/components/Box";
import ImagesCarousel from "@memorang/ui/components/ImagesCarousel";
import { trackCustomEvent } from "analytics";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";

/* -----------------Props--------------- */
type Props = {
	choices: Choice[];
	itemId: number;
	feedback?: ItemFeedback;
};

const RenderExplanation = ({
	explanation,
	fontSize,
	media,
}: {
	explanation: string | undefined;
	fontSize: number;
	media?: Media[];
}) => {
	if (explanation) {
		const normalizedExplanation = explanation.replace(/<br>/g, "\n\n");
		return (
			<Box>
				<CustomMarkdownRenderer
					text={normalizedExplanation}
					variant="explanation"
					fontSize={fontSize}
					paragraphMarginVertical={6}
				/>
				{media?.length ? <ImagesCarousel images={media} /> : null}
			</Box>
		);
	}
	return null;
};

/* -----------------Component--------------- */
const HumanExplanation = (props: Props) => {
	const { choices, itemId, feedback } = props;
	const { t } = useTranslation();

	const sortedChoices = getSortedChoices(choices);
	const correctChoices = sortedChoices.filter(({ isCorrect }) => isCorrect);

	const hasChoicesWithText = choices.some(({ text }) => text);

	const theme = useAppTheme();

	const fontSize = useAppStore((store) => store.app.fontSizes?.stem) || 16;

	const explanation = findFeedbackByLanguage(feedback)?.detailed?.text;
	const media = findFeedbackByLanguage(feedback)?.detailed?.media;
	const RenderChoice = ({ isCorrect, text }: Choice) => (
		<Box display="flex" flexDirection="row" gap={8} alignItems="center">
			{isCorrect ? (
				<Icon
					name="checkbox-marked-circle"
					color={theme.colors.progress.high}
					size={24}
				/>
			) : (
				<Icon name="close-circle" size={24} color={theme.colors.progress.low} />
			)}
			<Box flex={1}>
				<CustomMarkdownRenderer
					text={text}
					customStyles={{
						paragraph: {
							...theme.typography.fonts.medium,
						},
					}}
					fontSize={fontSize}
					variant="answer"
				/>
			</Box>
		</Box>
	);

	const ChoicesWithExplanation = () =>
		explanation ? (
			<RenderExplanation
				explanation={explanation}
				fontSize={fontSize}
				media={media}
			/>
		) : (
			<Box gap={8}>
				{sortedChoices.map((choice) => {
					return (
						<Fragment key={choice.id}>
							{hasChoicesWithText ? <RenderChoice {...choice} /> : null}
							{explanation && (
								<RenderExplanation
									explanation={explanation}
									fontSize={fontSize}
								/>
							)}
						</Fragment>
					);
				})}
			</Box>
		);

	if (correctChoices.length > 1) {
		return (
			<Box gap={16}>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					<CustomListSubheaderContainer enableGutters={false}>
						{t("session.explanation.title")}
					</CustomListSubheaderContainer>
					{explanation && (
						<TTSContainer
							itemId={`${itemId}`}
							eventName={events.ttsClicked}
							trackCustomEvent={trackCustomEvent}
							explanation={explanation}
							size={18}
							allowPrefetch={true}
						/>
					)}
				</Box>

				<Box gap={8}>
					{correctChoices.map((choice) => (
						<Box key={choice.id}>
							<RenderChoice {...choice} />
						</Box>
					))}
					{explanation && (
						<RenderExplanation
							explanation={explanation}
							fontSize={fontSize}
							media={media}
						/>
					)}
				</Box>
			</Box>
		);
	}

	return (
		<Box>
			<Box
				flexDirection="row"
				alignItems="center"
				justifyContent="space-between"
			>
				<CustomListSubheaderContainer
					enableGutters={false}
					textVariant="titleMedium"
				>
					{t("session.explanation.title")}
				</CustomListSubheaderContainer>
				{explanation && (
					<TTSContainer
						itemId={`${itemId}`}
						explanation={explanation}
						size={18}
						allowPrefetch={true}
						eventName={events.ttsClicked}
						trackCustomEvent={trackCustomEvent}
					/>
				)}
			</Box>
			<ChoicesWithExplanation />
		</Box>
	);
};

export default HumanExplanation;
